import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Fade from '@rio-cloud/rio-uikit/Fade';
import Notification from '@rio-cloud/rio-uikit/Notification';
import throttle from 'lodash/throttle';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type IntlShape, useIntl } from 'react-intl';

import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { isStopEditable } from '#domain/isStopEditable.ts';
import { type CapacityLimits, getTourLoadMetrics, hasExceededTourCapacity } from '#domain/loadMetrics.ts';
import type { Order } from '#domain/Order.ts';
import { isServiceOrder } from '#domain/Order.ts';
import { isTourStarted, type TourDetails } from '#domain/Tour.ts';
import { canRemoveStopFromTour, getStopsFromTour, mapStopTypeToStop, type TourStop } from '#domain/TourStop.ts';
import { toCapacityWeightKg } from '#domain/tourCapacity.ts';
import { EditStopDialog } from '#features/stopEdit/ui/EditStopDialog.tsx';
import { useHandleTourStops } from '#features/tourCreate/hooks/useHandleTourStops.tsx';
import { useSmartRoute } from '#features/tourCreate/hooks/useSmartRoute.ts';
import { useTourSave } from '#features/tourCreate/hooks/useTourSave.ts';
import { DEFAULT_ROUTING_SETTINGS, type RoutingSettings } from '#features/tourCreate/model/routingSettings.ts';
import { validateEditedStopOrder } from '#features/tourCreate/model/validateEditedStopOrder.ts';
import { useGetDriverOptions } from '#hooks/resources/useGetDriverOptions.ts';

import type { TourFormValues } from '../model/NewTourFormState.ts';
import { TourFormValidation } from '../model/TourFormValidation.ts';
import { CapacityExceededConfirmationDialog } from './CapacityExceededConfirmationDialog.tsx';
import { DialogFooter } from './DialogFooter.tsx';
import { SmartRouteProgress } from './SmartRouteProgress.tsx';
import { TourForm } from './TourForm.tsx';
import { TourPlanningContent } from './TourPlanningContent.tsx';
import { VehicleRoutingSettingsDialog } from './VehicleRoutingSettingsDialog.tsx';
import './tourCreate.css';

export type DialogProps = {
    close: () => void;
    isOpen: boolean;
    orders: Order[];
    availableOrders: Order[];
    isLoadingOrders?: boolean;
    isOrderError?: boolean;
    onRetryOrders?: () => void;
    tourToEdit?: TourDetails;
};

const createDefaultValues = (selectedOrders: Order[], tourToEdit?: TourDetails): TourFormValues => ({
    tourNumber: tourToEdit?.searchName ?? '',
    note: tourToEdit?.note ?? '',
    driver: tourToEdit?.driverId ?? '',
    vehicle: tourToEdit?.truck.id ?? '',
    selectedOrderIds: selectedOrders.map(order => order.id),
    maxWeightT: tourToEdit?.capacity?.maxWeightKg === undefined ? undefined : tourToEdit.capacity.maxWeightKg / 1000,
    maxLoadingMeter: tourToEdit?.capacity?.maxLoadingMeter,
    maxVolumeLiter: tourToEdit?.capacity?.maxVolumeLiter,
    maxVolumeCubicMeter: tourToEdit?.capacity?.maxVolumeCubicMeter,
});

const showOrderCanNotBeRemovedFormTourNotification = (intl: IntlShape) =>
    Notification.error(
        intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.edit.remove-unload-after-finished-load.error',
        })
    );

export const CreateTourDialog = memo(
    ({
        close,
        isOpen,
        orders,
        availableOrders,
        tourToEdit,
        isLoadingOrders,
        isOrderError,
        onRetryOrders,
    }: DialogProps) => {
        const intl = useIntl();
        const [stopToEdit, setStopToEdit] = useState<TourStop>();
        const [routingSettings, setRoutingSettings] = useState<RoutingSettings>(DEFAULT_ROUTING_SETTINGS);
        const [showVehicleSettings, setShowVehicleSettings] = useState(false);
        useEffect(() => {
            setRoutingSettings(DEFAULT_ROUTING_SETTINGS);
            setShowVehicleSettings(false);
        }, [isOpen, tourToEdit?.id]);
        const [pendingTourPayload, setPendingTourPayload] = useState<TourFormValues>();
        const showSmartRoute = useFeatureToggle('showSmartRoute', false);

        const driverOptions = useGetDriverOptions();

        const isEditMode = Boolean(tourToEdit);
        const dialogTitle = isEditMode
            ? intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.title',
              })
            : intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.title',
              });
        const dialogTitleId = isEditMode
            ? 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.title'
            : 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.title';
        const submitLabel = isEditMode
            ? intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.save',
              })
            : dialogTitle;
        const submitTrackingKey = isEditMode
            ? 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.save'
            : dialogTitleId;

        const {
            control,
            formState: { errors, isDirty },
            handleSubmit,
            reset,
            setValue,
            watch,
        } = useForm<TourFormValues>({
            defaultValues: createDefaultValues(orders, tourToEdit),
            resolver: zodResolver(TourFormValidation),
        });

        const selectableOrders = availableOrders;
        const selectedOrderIds = watch('selectedOrderIds');
        const maxWeightT = watch('maxWeightT');
        const maxLoadingMeter = watch('maxLoadingMeter');
        const maxVolumeLiter = watch('maxVolumeLiter');
        const maxVolumeCubicMeter = watch('maxVolumeCubicMeter');
        const capacity = {
            maxWeightKg: toCapacityWeightKg(maxWeightT),
            maxLoadingMeter,
            maxVolumeLiter,
            maxVolumeCubicMeter,
        };
        const capacityLimits: CapacityLimits = {
            weight: capacity.maxWeightKg,
            loadingMeter: capacity.maxLoadingMeter,
            liter: capacity.maxVolumeLiter,
            cubicMeter: capacity.maxVolumeCubicMeter,
        };
        const selectedVehicleId = watch('vehicle');
        const selectedOrderIdsKey = selectedOrderIds.join('|');
        const selectedOrders = useMemo(() => {
            const orderById = new Map(selectableOrders.map(order => [order.id, order]));
            return selectedOrderIds.flatMap(orderId => {
                const order = orderById.get(orderId);
                return order ? [order] : [];
            });
        }, [selectableOrders, selectedOrderIds]);
        const orderByStopId = useMemo(() => {
            const ordersByStopId = new Map<string, Order>();
            selectedOrders.forEach(order => {
                if (isServiceOrder(order)) {
                    ordersByStopId.set(order.executionStop.id, order);
                } else {
                    ordersByStopId.set(order.loadingStop.id, order);
                    ordersByStopId.set(order.unloadingStop.id, order);
                }
            });
            return ordersByStopId;
        }, [selectedOrders]);

        const initialStops = useMemo(() => {
            if (!tourToEdit) {
                return undefined;
            }
            return getStopsFromTour(tourToEdit, selectedOrders);
        }, [selectedOrderIdsKey, tourToEdit?.id]);

        const selectedOrderById = useMemo(
            () => new Map(selectedOrders.map(order => [order.id, order])),
            [selectedOrders]
        );

        const { sortableTourStops, setSortableTourStops, isMisplaced, misplacedStopIds } = useHandleTourStops(
            isOpen,
            selectedOrders,
            initialStops
        );
        const smartRoute = useSmartRoute(
            sortableTourStops,
            JSON.stringify([tourToEdit?.id, selectedVehicleId, capacity]),
            isOpen && showSmartRoute,
            routingSettings
        );
        const exceededCapacityDimensions = [
            ...new Set(
                [...getTourLoadMetrics(sortableTourStops, selectedOrders, capacityLimits).values()].flatMap(
                    metrics => metrics.exceededCapacity ?? []
                )
            ),
        ];
        const tourStarted = isTourStarted(tourToEdit);
        const editableStopIds = useMemo(() => {
            return new Set(
                sortableTourStops
                    .filter(stop =>
                        isStopEditable(
                            mapStopTypeToStop(stop),
                            orderByStopId.get(stop.stopId ?? stop.id),
                            tourToEdit?.status
                        )
                    )
                    .map(stop => stop.stopId ?? stop.id)
            );
        }, [orderByStopId, sortableTourStops, tourToEdit?.status]);

        const updateStopInTourDialog = (editedStop: TourStop) => {
            setSortableTourStops(
                sortableTourStops.map(stop =>
                    (stop.stopId ?? stop.id) === (editedStop.stopId ?? editedStop.id) ? editedStop : stop
                )
            );
        };

        const updateSelectedOrderIds = useCallback(
            (nextSelectedOrderIds: string[]) => {
                setValue('selectedOrderIds', nextSelectedOrderIds, { shouldValidate: true, shouldDirty: true });
            },
            [setValue]
        );

        const removeStopFromTour = useCallback(
            (stop: TourStop) => {
                const order = selectedOrderById.get(stop.orderId);
                if (!canRemoveStopFromTour(sortableTourStops, stop, order)) {
                    showOrderCanNotBeRemovedFormTourNotification(intl);
                    return;
                }
                updateSelectedOrderIds(selectedOrderIds.filter(selectedOrderId => selectedOrderId !== stop.orderId));
            },
            [intl, selectedOrderById, selectedOrderIds, sortableTourStops, updateSelectedOrderIds, setSortableTourStops]
        );

        const handleSelectedOrderIdsChange = useCallback(
            (nextSelectedOrderIds: string[]) => {
                const removedOrderIds = selectedOrderIds.filter(orderId => !nextSelectedOrderIds.includes(orderId));
                const removedStops = sortableTourStops.filter(stop => removedOrderIds.includes(stop.orderId));
                const hasBlockedRemovedStop = removedStops.some(
                    stop => !canRemoveStopFromTour(sortableTourStops, stop, selectedOrderById.get(stop.orderId))
                );
                if (hasBlockedRemovedStop) {
                    showOrderCanNotBeRemovedFormTourNotification(intl);
                    setValue('selectedOrderIds', selectedOrderIds, { shouldValidate: true });
                    return;
                }
                updateSelectedOrderIds(nextSelectedOrderIds);
            },
            [intl, selectedOrderById, selectedOrderIds, setValue, sortableTourStops, updateSelectedOrderIds]
        );

        useEffect(() => {
            if (!isOpen) {
                setPendingTourPayload(undefined);
                return;
            }

            if (!isDirty) {
                reset(createDefaultValues(orders, tourToEdit));
            }
        }, [isDirty, isOpen, orders, reset, tourToEdit]);

        const handleClose = useCallback(() => {
            smartRoute.cancel();
            reset(createDefaultValues([]));
            setStopToEdit(undefined);
            setPendingTourPayload(undefined);
            close();
        }, [close, reset, smartRoute.cancel]);

        const { isLoading, notificationsEnabled, setNotificationsEnabled, submitTourPayload } = useTourSave({
            isOpen,
            tourToEdit,
            sortableTourStops,
            onSaved: handleClose,
        });

        const submitTour = throttle(
            handleSubmit(async payload => {
                if (isMisplaced) {
                    return;
                }
                if (hasExceededTourCapacity(sortableTourStops, selectedOrders, capacityLimits)) {
                    setPendingTourPayload(payload);
                    return;
                }

                await submitTourPayload(payload);
            }),
            500,
            { leading: true }
        );

        const confirmTourSave = async () => {
            const payload = pendingTourPayload;
            setPendingTourPayload(undefined);
            if (payload) {
                await submitTourPayload(payload);
            }
        };

        return (
            <Dialog
                show={isOpen}
                title={dialogTitle}
                body={
                    <Fade>
                        <TourForm
                            control={control}
                            isTourStarted={tourStarted}
                            driverOptions={driverOptions}
                            selectedOrders={selectedOrders}
                            exceededCapacityDimensions={exceededCapacityDimensions}
                        />
                        <TourPlanningContent
                            openOrdersProps={{
                                isOpen,
                                isLoading: isLoadingOrders,
                                isError: isOrderError,
                                onRetry: onRetryOrders,
                                orders: selectableOrders,
                                selectedOrderIds,
                                onSelectedOrderIdsChange: handleSelectedOrderIdsChange,
                            }}
                            stopListProps={{
                                sortableTourStops,
                                setSortableTourStops,
                                orders: selectedOrders,
                                misplacedStopIds,
                                onRemoveStop: removeStopFromTour,
                                editableStopIds,
                                onEditStop: setStopToEdit,
                                capacity,
                            }}
                            vehicleError={errors.vehicle}
                            selectedVehicleId={selectedVehicleId}
                            onSelectVehicle={vehicleId =>
                                setValue('vehicle', vehicleId, { shouldValidate: true, shouldDirty: true })
                            }
                            tourId={tourToEdit?.id}
                            isOpen={isOpen}
                            isLoading={isLoading}
                            isRouting={Boolean(smartRoute.stage)}
                            isTourStarted={tourStarted}
                            showSmartRoute={showSmartRoute}
                            onShowVehicleSettings={() => setShowVehicleSettings(true)}
                            routingSettings={routingSettings}
                            routeResult={smartRoute.result}
                        />
                        {showVehicleSettings && (
                            <VehicleRoutingSettingsDialog
                                value={routingSettings}
                                onClose={() => setShowVehicleSettings(false)}
                                onApply={settings => {
                                    setRoutingSettings(settings);
                                    setShowVehicleSettings(false);
                                }}
                            />
                        )}
                        {stopToEdit && (
                            <EditStopDialog
                                isOpen={!!stopToEdit}
                                stop={mapStopTypeToStop(stopToEdit)}
                                validateStop={updatedStop =>
                                    validateEditedStopOrder(updatedStop, stopToEdit, sortableTourStops)
                                }
                                onClose={() => setStopToEdit(undefined)}
                                onSubmit={(stopUpdate, updatedStop) => {
                                    updateStopInTourDialog({
                                        ...stopToEdit,
                                        name: updatedStop.name,
                                        location: updatedStop.location,
                                        start: updatedStop.startTarget,
                                        end: updatedStop.endTarget,
                                        latitude: updatedStop.latitude,
                                        longitude: updatedStop.longitude,
                                        comment: updatedStop.comment,
                                        stopUpdate,
                                    });
                                }}
                            />
                        )}
                        <SmartRouteProgress
                            stage={smartRoute.stage}
                            onCancel={smartRoute.cancel}
                            onRetry={smartRoute.retry}
                        />
                        <CapacityExceededConfirmationDialog
                            mode={isEditMode ? 'edit' : 'create'}
                            show={pendingTourPayload !== undefined}
                            onEdit={() => setPendingTourPayload(undefined)}
                            onConfirm={confirmTourSave}
                        />
                    </Fade>
                }
                footer={
                    <DialogFooter
                        notificationsEnabled={notificationsEnabled}
                        setNotificationsEnabled={setNotificationsEnabled}
                        onSubmitTour={submitTour}
                        isMisplaced={isMisplaced || selectedOrders.length === 0}
                        isLoading={isLoading}
                        submitLabel={submitLabel}
                        submitTrackingKey={submitTrackingKey}
                        extraAction={
                            showSmartRoute && (
                                <span
                                    className='display-flex flex-wrap gap-10'
                                    title={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.smart-route.requirements',
                                    })}
                                >
                                    <Button
                                        bsStyle='success'
                                        iconName='route'
                                        disabled={
                                            !smartRoute.canCalculate ||
                                            isLoading ||
                                            isMisplaced ||
                                            Boolean(smartRoute.stage)
                                        }
                                        onClick={() => smartRoute.calculate()}
                                    >
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.smart-route.button',
                                        })}
                                    </Button>
                                    <Button
                                        bsStyle='success'
                                        iconName='route'
                                        disabled={
                                            !smartRoute.canCalculate ||
                                            isLoading ||
                                            isMisplaced ||
                                            Boolean(smartRoute.stage)
                                        }
                                        onClick={() => smartRoute.calculate('ev')}
                                    >
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.smart-route.ev-button',
                                        })}
                                    </Button>
                                </span>
                            )
                        }
                    />
                }
                onClose={handleClose}
                disableEsc={showVehicleSettings}
                bodyClassName='tour-planning padding-20 bg-lightest'
                bsSize='fullscreen'
                showCloseButton={false}
                headerButtons={
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        iconName='remove'
                        autoTrackingKey={`${dialogTitleId}.close`}
                        className='modal-header-close close'
                        onClick={handleClose}
                        aria-label='dialog close button'
                    />
                }
            />
        );
    },
    (prevProps, nextProps) =>
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.tourToEdit?.id === nextProps.tourToEdit?.id &&
        prevProps.orders === nextProps.orders &&
        prevProps.availableOrders === nextProps.availableOrders &&
        prevProps.isLoadingOrders === nextProps.isLoadingOrders &&
        prevProps.isOrderError === nextProps.isOrderError &&
        prevProps.onRetryOrders === nextProps.onRetryOrders
);
